import React, { useEffect, useState } from 'react';
import { Button, Modal } from 'tea-component';
import { useIntl } from 'umi';
import './index.scss';

/**
 * 通用「可编辑弹窗」公共组件
 *
 * 设计目的：
 * - 外部通过 `value` 传入需要编辑的数据
 * - 组件内部维护一份「草稿态」草稿，弹窗内对草稿进行修改，不影响外部数据
 * - 点击「确定」：通过 `onConfirm(draft)` 将草稿同步回外部，并关闭弹窗
 * - 点击「取消」/关闭：丢弃草稿，不做任何同步
 *
 * 使用方式（render props）：
 * ```tsx
 * <EditableModal
 *   visible={visible}
 *   title="编辑设置"
 *   value={{ prompt, gptOptions }}
 *   onConfirm={(draft) => { syncDraftToExternal(draft); }}
 *   onClose={() => setVisible(false)}
 * >
 *   {({ prompt, gptOptions }, setDraft) => (
 *     <Input
 *       value={prompt}
 *       onChange={(v) => setDraft((d) => ({ ...d, prompt: v }))}
 *     />
 *   )}
 * </EditableModal>
 * ```
 */
export interface EditableModalProps<T = any> {
  /** 是否显示 */
  visible: boolean;
  /** 弹窗标题 */
  title?: React.ReactNode;
  /** 外部传入的初始数据，弹窗打开时会用它初始化内部草稿 */
  value: T;
  /** 点击「确定」时回调，参数为当前草稿数据，用于同步回外部 */
  onConfirm: (draft: T) => void;
  /** 关闭弹窗（取消/关闭），不触发数据同步 */
  onClose: () => void;
  /** 渲染弹窗内容，接收草稿数据与 setter */
  children: (
    draft: T,
    setDraft: React.Dispatch<React.SetStateAction<T>>,
  ) => React.ReactNode;
  /** 确定按钮文案 */
  okText?: string;
  /** 取消按钮文案 */
  cancelText?: string;
  /** 确定按钮是否禁用 */
  confirmDisabled?: boolean;
  /** 弹窗宽度 */
  size?: number | 'small' | 'medium' | 'large' | 'full';
  /** 弹窗样式类名 */
  className?: string;
  /** 关闭时是否销毁内容（默认销毁，以保证草稿在下次打开时按外部数据重新初始化） */
  destroyOnClose?: boolean;
  /** 点击确定后的额外回调（例如埋点），不参与数据同步 */
  onOk?: (draft: T) => void;
}

function EditableModal<T = any>({
  visible,
  title,
  value,
  onConfirm,
  onClose,
  children,
  okText,
  cancelText,
  confirmDisabled = false,
  size = 800,
  className,
  destroyOnClose = true,
  onOk,
}: EditableModalProps<T>) {
  const { formatMessage } = useIntl();

  // 内部草稿态，仅在弹窗内修改
  const [draft, setDraft] = useState<T>(value);

  // 每次弹窗打开时，用外部数据重新初始化草稿，保证草稿不与外部脱节
  useEffect(() => {
    if (visible) {
      setDraft(value);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [visible]);

  const handleCancel = () => {
    onClose?.();
  };

  const handleConfirm = () => {
    onConfirm?.(draft);
    onOk?.(draft);
    onClose?.();
  };

  return (
    <Modal
      visible={visible}
      caption={title}
      onClose={handleCancel}
      size={size}
      destroyOnClose={destroyOnClose}
      className={className}
    >
      <Modal.Body>{children(draft, setDraft)}</Modal.Body>
      <Modal.Footer>
        <Button
          type="primary"
          onClick={handleConfirm}
          disabled={confirmDisabled}
        >
          {okText ?? formatMessage({ id: 'Confirm' })}
        </Button>
        <Button type="weak" onClick={handleCancel}>
          {cancelText ?? formatMessage({ id: 'Cancel' })}
        </Button>
      </Modal.Footer>
    </Modal>
  );
}

export default EditableModal;
